import React from 'react';
import { useAtomValue } from 'jotai';
import { Separator } from '@rocco/components/separator';
import { formatNumberWithCommasAndDecimals } from '@rocco/utils/format-number';
import { SEARCH_CATEGORIES } from '@modules-rocco/search/config/search-service.config';
import { searchPageDataAtom } from '../states/search-page-data-atom';
import { Skeleton } from '@archipro/rocco/components/skeleton';

/**
 * Renders the header above the search results grid.
 * Displays the total result count and the search query/category.
 * Includes a separator for desktop view.
 */
export const SearchPageGridHeader: React.FC<{ isLoading: boolean }> = ({
    isLoading,
}) => {
    const pageDataFromAtom = useAtomValue(searchPageDataAtom);
    const totalCount = pageDataFromAtom?.totalCount ?? 0;
    const searchQuery = pageDataFromAtom?.searchQuery ?? '';
    const searchIndex = pageDataFromAtom?.searchIndex;
    const appDisplayMode = pageDataFromAtom?.appDisplayMode;

    const isMobile = appDisplayMode === 'mobile';
    const hasSearchQuery = searchQuery !== '';

    if (!hasSearchQuery || !pageDataFromAtom) {
        // Return null if no search query or if essential data isn't loaded yet
        return null;
    }

    const categoryName = searchIndex
        ? SEARCH_CATEGORIES.find((c) => c.index === searchIndex)?.children
        : '';
    const titleText = `${formatNumberWithCommasAndDecimals(totalCount)} results for '${searchQuery}'${categoryName ? ` in ${categoryName}` : ''}`;

    return (
        <>
            {!isMobile && <Separator />}
            {isLoading ? (
                <div className="px-6 md:hidden">
                    <Skeleton className="h-12 w-full" variant="light" />
                </div>
            ) : (
                <div className="mb-0.5 flex items-center justify-between px-6 pb-0.5 pt-4 md:px-0 md:pb-2.5 md:pt-5">
                    <span className="text-lg font-medium tracking-[0.025rem]">
                        {titleText}
                    </span>
                </div>
            )}
        </>
    );
};
